/**
 * @voltxampere/ui — Operational Amplifier (Op-Amp)
 *
 * Triangle pointing right, two inputs at the flat side (− at top,
 * + at bottom), single output at the tip. The supply rails (V+ /
 * V−) are NOT drawn at the symbol level — Sprint 11+ rail-tying is
 * handled at the schematic layer where it can be hidden by default
 * for cleaner block diagrams.
 *
 * Sprint 25 R3 lane 2 — replaced the `<text>` polarity glyphs with
 * line-glyphs. The `svg-compile` runtime intentionally skips
 * `<text>` elements so the pre-Sprint-25 op-amp rendered without
 * its `+`/`−` markers (TS-006 follow-up + TRIAGE_LANE4_FINDINGS.md
 * §3 OPAMP-TEXT-RENDER lane). Same line-glyph pattern as
 * Sprint 25 R3 lane 1's dc-source polarity bars.
 *
 * v1 reference: `~/Desktop/VoltXAmpere/src/components/definitions.js`
 * `opamp` block draws the `+`/`−` text via canvas `fillText`.
 * v3 ports the *intent* (visible polarity markers) to line-glyphs
 * because the canvas pipeline is SVG-compiled, not direct
 * Canvas2D, and the compile runtime does not implement text.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { STROKE_IVORY, STROKE_WIDTH_MAIN, VIEWBOX_DEFAULT } from "./constants.js";
export const opAmp = {
    id: "op-amp",
    nameTr: "Op-Amp",
    nameEn: "Operational Amplifier",
    shortcut: "O",
    category: "active",
    viewBox: VIEWBOX_DEFAULT,
    terminals: [
        { x: 0, y: 9, label: "−", polarity: "in" },
        { x: 0, y: 17, label: "+", polarity: "in" },
        { x: 40, y: 13, label: "out", polarity: "out" },
    ],
    defaultLabel: "U",
    renderSVG: () => [
        // Triangle body — flat left, tip right.
        `<polygon points="10,4 10,22 30,13" fill="none" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        // Pin lead lines — two inputs (left) + one output (right).
        `<line x1="0" y1="9" x2="10" y2="9" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        `<line x1="0" y1="17" x2="10" y2="17" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        `<line x1="30" y1="13" x2="40" y2="13" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        // Sprint 25 R3 lane 2 — `−` line-glyph next to the inverting
        // input pin (pin at (0,9), glyph centered at (13, 10) inside
        // the triangle).
        `<line x1="11" y1="10" x2="15" y2="10" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        // `+` line-glyph next to the non-inverting input pin (pin at
        // (0,17), glyph centered at (13, 18)).
        `<line x1="11" y1="18" x2="15" y2="18" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
        `<line x1="13" y1="16" x2="13" y2="20" stroke="${STROKE_IVORY}" stroke-width="${String(STROKE_WIDTH_MAIN)}"/>`,
    ].join(""),
};
//# sourceMappingURL=op-amp.js.map